import React, {FC} from 'react';

import Panel from '@/components/Panels/common/Panel';

import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';

import LayersIcon from '@mui/icons-material/Layers';
import MapIcon from '@mui/icons-material/Map';
import BookmarksIcon from '@mui/icons-material/Bookmarks';

import BaseMapList from '@geomatico/geocomponents/Map/BaseMapList';
import LayerSwitcher from '@geomatico/geocomponents/Map/LayerSwitcher';

import {MAPSTYLES, PRINCIPADO_OVERLAYS, PANEL_WIDTH, CETEMAS_OVERLAYS, CATASTRO_OVERLAY, PROJECT_OVERLAY} from '@/config';
import {useTranslation} from 'react-i18next';
import {User, USER_ROLE} from '@/domain/entities/User';
import {Project} from '@/domain/entities/Project';

//TYPES
export type SettingsPanelProps = {
  project?: Project,
  user?: User,
  mapStyle: string,
  onMapStyleChanged: (style: string) => void,
  selectedOverlays: Array<string>,
  onOverlaysChanged: (overlays: Array<string>) => void
};

const SettingsPanel: FC<SettingsPanelProps> = ({project, user, mapStyle, onMapStyleChanged, selectedOverlays, onOverlaysChanged}) => {

  const {t} = useTranslation();
  const hasPermissions = user?.role === USER_ROLE.ADMIN || user?.role === USER_ROLE.TECHNICIAN;

  const otherLayers = hasPermissions
    ? project 
      ? [...CATASTRO_OVERLAY, ...CETEMAS_OVERLAYS(project.zone.toLowerCase())]
      : CATASTRO_OVERLAY
    : CATASTRO_OVERLAY;

  return <Panel width={PANEL_WIDTH.settings}>
    <Stack direction="row" sx={{justifyContent: 'space-between', width: '100%', gap: 2, height: '600px'}}>

      {/*PRIMERA COLUMNA*/}
      <Stack sx={{width: '50%', flexShrink: 0}}>
        <Stack direction="column" sx={{paddingBottom: 2}}>
          <Stack direction="row" sx={{alignItems: 'center', gap: 1}}>
            <MapIcon sx={{fontSize: '28px', color: 'primary.main'}}/>
            <Typography variant="h6" sx={{color: 'primary.main', m: 0, p: 0}}>{t('settingsPanel.baseMapStyle')}</Typography>
          </Stack>
          <Typography variant="body2" sx={{color: 'text.secondary', pl: 0.5}}>{t('settingsPanel.baseMapStyleDescription')}</Typography>
        </Stack>
        <Stack>
          <BaseMapList
            thumbnailWidth={72}
            thumbnailHeight={40}
            variant="list"
            styles={MAPSTYLES}
            selectedStyleId={mapStyle}
            onStyleChange={onMapStyleChanged}
            itemWidth={100}
          />
        </Stack>

        <Divider orientation="horizontal" flexItem sx={{m: 0.5}}/>

        <Stack direction="column" sx={{py: 2}}>
          <Stack direction="row" sx={{alignItems: 'center', gap: 1}}>
            <BookmarksIcon sx={{fontSize: '28px', color: 'primary.main'}}/>
            <Typography variant="h6" sx={{color: 'primary.main', m: 0, p: 0}}>{t('settingsPanel.projectOverlays')}</Typography>
          </Stack>
          <Typography variant="body2" sx={{color: 'text.secondary', pl: 0.5}}>{t('settingsPanel.projectOverlaysDescription')}</Typography>
        </Stack>
        <Stack>
          <LayerSwitcher
            thumbnailWidth={40}
            thumbnailHeight={40}
            variant="list"
            exclusive={false}
            allowEmptyValue={true}
            layers={PROJECT_OVERLAY}
            selectedLayerIds={selectedOverlays || []}
            onLayerChange={onOverlaysChanged}
            itemWidth={200}
          />
        </Stack>

      </Stack>
      <Divider orientation="vertical" flexItem sx={{m: 0.5}}/>
      
      {/*SEGUNDA COLUMNA*/}
      <Stack sx={{width: '50%', height: '100%', overflowY: 'auto', flex: 1}}>
        <Stack direction="column" sx={{paddingBottom: 2}}>
          <Stack direction="row" sx={{alignItems: 'center', gap: 1}}>
            <LayersIcon sx={{fontSize: '28px', color: 'primary.main'}}/>
            <Typography variant="h6" sx={{color: 'primary.main', m: 0, p: 0}}>{t('settingsPanel.principadoOverlays')}</Typography>
          </Stack>
          <Typography variant="body2" sx={{color: 'text.secondary', pl: 0.5}}>{t('settingsPanel.overlaysDescription')}</Typography>
        </Stack>
        <Stack>
          <LayerSwitcher
            thumbnailWidth={40}
            thumbnailHeight={40}
            variant="list"
            exclusive={false}
            allowEmptyValue={true}
            layers={PRINCIPADO_OVERLAYS}
            selectedLayerIds={selectedOverlays || []}
            onLayerChange={onOverlaysChanged}
            itemWidth={200}
          />
        </Stack>
        <Divider orientation="horizontal" flexItem sx={{pt: 0.5, m: 2}}/>
        <Stack>
          <Stack direction="column" sx={{paddingBottom: 2}}>
            <Stack direction="row" sx={{alignItems: 'center', gap: 1}}>
              <LayersIcon sx={{fontSize: '28px', color: 'primary.main'}}/>
              <Typography variant="h6" sx={{color: 'primary.main', m: 0, p: 0}}>{t('settingsPanel.cetemasOverlays')}</Typography>
            </Stack>
            <Typography variant="body2" sx={{color: 'text.secondary', pl: 0.5}}>{t('settingsPanel.overlaysDescription')}</Typography>
          </Stack>
          <Stack>
            <LayerSwitcher
              thumbnailWidth={40}
              thumbnailHeight={40}
              variant="list"
              exclusive={false}
              allowEmptyValue={true}
              layers={otherLayers}
              selectedLayerIds={selectedOverlays || []}
              onLayerChange={onOverlaysChanged}
              itemWidth={200}
            />
          </Stack>
        </Stack>
      </Stack>
      
    </Stack>
  </Panel>;
};
export default SettingsPanel;